import {
  AutocompleteArrayInput,
  Button,
  Form,
  Loading,
  NumberInput,
  ReferenceArrayInput,
  SaveButton,
  SelectInput,
  useCheckAuth,
  useGetIdentity,
} from 'react-admin';
import React, { useEffect, useState } from 'react';
import { AssetRef, ZoneRef } from '@x-guard/xgac-types/xgac';
import {
  Dialog, DialogContent, DialogTitle, IconButton, useTheme,
} from '@mui/material';
import { useTranslate } from 'ra-core';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import ReactApexChart from 'react-apexcharts';
import { reportDataProvider } from '../../../dataProviders/reportDataProvider';
import { getCurrentCustomer } from '../../../lib/currentCustomer';
import { xgacDataProvider } from '../../../dataProviders/xgacDataProvider';
import { BooleanCheckBoxInput } from '../../bhvk/components/inputs/BooleanCheckBoxInput';

const granularityMapping = {
  '1hour': { timeframe: { type: 'hour', value: 1, floorTo: 'minute' }, granularity: { type: 'minute', value: 10 } },
  '8hours': { timeframe: { type: 'hour', value: 8, floorTo: 'minute' }, granularity: { type: 'minute', value: 15 } },
  '1day': { timeframe: { type: 'day', value: 1, floorTo: 'minute' }, granularity: { type: 'minute', value: 30 } },
  '1week': { timeframe: { type: 'week', value: 1, floorTo: 'hour' }, granularity: { type: 'minute', value: 30 } },
};
export const ZoneOverview = () => {

  const translate = useTranslate();
  const checkAuth = useCheckAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const currentCustomer = getCurrentCustomer();
  const identity = useGetIdentity();
  checkAuth();
  const [reportResult, setReportResult] = useState<any>(null);
  const [savedCurrentCustomer, setSavedCurrentCustomer] = useState<string>(currentCustomer?.value || '');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasZones, setHasZones] = useState<boolean | null>(null);
  const [hasGroups, setHasGroups] = useState<boolean>(false);
  const [settings, setSettings] = useState<{
    minOccupancy: number;
    includeGroups: AssetRef[];
    zones: ZoneRef[];
    timeFrame: string;
    inactivityThreshold: boolean;
  }>(JSON.parse(localStorage.getItem(`zoneOverviewSettings-${savedCurrentCustomer}`) || 'null') || {
    minOccupancy: 0,
    includeGroups: [],
    zones: [],
    timeFrame: '1day',
    inactivityThreshold: false,
  });

  useEffect(() => {

    const getCustomerSettings = async () => {

      const savedSettings = JSON.parse(localStorage.getItem(`zoneOverviewSettings-${currentCustomer?.value}`) || 'null');

      if (savedSettings) {

        setSettings(savedSettings as any);
        return;

      }
      const trigger = await xgacDataProvider.getList('report-triggers', {
        pagination: {
          page: 1,
          perPage: 1,
        },
        sort: {
          field: 'createdAt',
          order: 'DESC',
        },
        filter: {
          'config.type': 'zoneOverview',
        },
      });

      if (trigger.total === 0) {

        setSettings({
          minOccupancy: 0,
          includeGroups: [],
          zones: [],
          timeFrame: '1day',
          inactivityThreshold: false,
        });

        if (!settingsOpen && hasZones !== false) {

          setSettingsOpen(true);

        }

      } else {

        const triggerData = trigger.data[0];
        const triggerSettings = {
          minOccupancy: triggerData.config.minOccupancy.assetCount || 0,
          includeGroups: triggerData.config.includeGroups || [],
          zones: triggerData.config.zones || [],
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          timeFrame: _.find(Object.keys(granularityMapping), (key) => _.isEqual(granularityMapping[key], triggerData.config.timeSeries)) || '1day',
          inactivityThreshold: !!triggerData.config.inactivityThreshold,
        };

        setSettings(triggerSettings as any);

      }

    };
    const getHasZonesAndGroups = async () => {

      const zones = await xgacDataProvider.getList('zones', {
        pagination: {
          page: 1,
          perPage: 1,
        },
        sort: {
          field: 'createdAt',
          order: 'DESC',
        },
        filter: {},
      });

      const groups = await xgacDataProvider.getList('asset-groups', {
        pagination: {
          page: 1,
          perPage: 1,
        },
        sort: {
          field: 'createdAt',
          order: 'DESC',
        },
        filter: {},
      });

      setHasZones(zones.total ? zones.total > 0 : false);
      setHasGroups(groups.total ? groups.total > 0 : false);

    };

    getCustomerSettings();
    getHasZonesAndGroups();

  }, [savedCurrentCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    if (currentCustomer?.value && currentCustomer?.value !== savedCurrentCustomer) {

      setSavedCurrentCustomer(currentCustomer?.value || '');

    }

  }, [identity, currentCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    const getReport = async () => {

      setIsLoading(true);
      const settingsValid = settings && settings.zones.length > 0;
      if (!settings || !settingsValid) {

        setReportResult(null);
        return;

      }

      const report = await reportDataProvider.getZoneOverview(
        settings.includeGroups,
        settings.zones,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        granularityMapping[settings.timeFrame || '1day'],
        settings.inactivityThreshold,
      );
      setReportResult(report);

    };

    getReport().then(() => {

      setIsLoading(false);

    });

  }, [settings]);

  const granularityOptions = [
    { id: '1hour', name: translate('resources.reports.text.hour', { smart_count: 1 }) },
    { id: '8hours', name: translate('resources.reports.text.hour', { smart_count: 8 }) },
    { id: '1day', name: translate('resources.reports.text.day', { smart_count: 1 }) },
    { id: '1week', name: translate('resources.reports.text.week', { smart_count: 1 }) },
  ];

  const ZoneSettings = () => {

    return (
      <>
        <div style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <IconButton onClick={() => setSettingsOpen(true)}>
            <SettingsIcon/>
          </IconButton>
        </div>
        <Dialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle className="flex-in-between">
            <span>{translate('resources.reports.text.zones_settings_title')}</span>
            <IconButton onClick={() => setSettingsOpen(false)}>
              <CloseIcon/>
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <div>
              <span>{translate('resources.reports.text.zones_settings_explain')}</span>
            </div>
            <Form
              defaultValues={{
                ...settings,
                includeGroups: settings.includeGroups.map((group: AssetRef) => group._id),
                zones: settings.zones.map((zone: ZoneRef) => zone._id),
              }}
              onSubmit={(values) => {

                const parsedValues = {
                  ...settings,
                  ...values,
                  includeGroups: values.includeGroups.map((group: string) => ({ _id: group, _ref: 'AssetGroup' })),
                  zones: values.zones.map((zone: string) => ({ _id: zone, _ref: 'Zone' })),
                };

                if (!_.isEqual(parsedValues, settings)) {

                  setSettings(parsedValues as any);

                }
                localStorage.setItem(`zoneOverviewSettings-${savedCurrentCustomer}`, JSON.stringify(parsedValues));
                setSettingsOpen(false);

              }}
            >
              <ReferenceArrayInput source="zones" reference="zones">
                <AutocompleteArrayInput fullWidth label="resources.zones.text.title"
                  helperText="resources.reports.text.zones_settings.zone_helpertext"/>
              </ReferenceArrayInput>
              <SelectInput
                source="timeFrame"
                choices={granularityOptions}
                fullWidth
                required
                helperText="resources.reports.text.zones_settings.period_helpertext"
                label={'resources.reports.text.zones_settings.period'}
              />
              <NumberInput
                source="minOccupancy"
                fullWidth
                label="resources.reports.fields.minOccupancy"
                helperText="resources.reports.text.zones_settings.min_occupancy_helpertext"
              />
              {hasGroups && (
                <ReferenceArrayInput source="includeGroups" reference="asset-groups">
                  <AutocompleteArrayInput
                    fullWidth
                    label="resources.asset-groups.name"
                    helperText={'resources.reports.text.zones_settings.includeGroups_helpertext'}
                  />
                </ReferenceArrayInput>
              )}
              <BooleanCheckBoxInput source={'inactivityThreshold'}
                helperLabel="resources.reports.text.zones_settings.inactivityThreshold"
                inputLabel={false}/>
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 10,
              }}>
                <SaveButton
                  label={'general.text.proceed'}
                  icon={<></>}
                  color={'secondary'}
                  alwaysEnable
                />
              </div>
            </Form>
          </DialogContent>
        </Dialog>
      </>
    );

  };

  const ZoneGraph = (data: any) => {

    // the data is in the form of {assetsInside: number, timestamp: string}. It should be converted to chart.js format
    const mappedData = data.occupancyHistory.map((value: any) => ({
      x: new Date(moment(value.timestamp).tz('Europe/Amsterdam').format()),
      y: value.assetsInside,
      assetIndexes: value.assetIndexes,
    }));

    const minOccupancyLine = [
      {
        x: mappedData[0].x,
        y: settings.minOccupancy,
      },
      {
        x: mappedData[mappedData.length - 1].x,
        y: settings.minOccupancy,
      },
    ];

    const minRange = mappedData[0].x;
    const maxRange = mappedData[mappedData.length - 1].x;

    const [dateRange, setDateRange] = useState({
      min: minRange,
      max: maxRange,
    });

    const maxY = Math.max(...mappedData.map((value: any) => value.y));
    const longDateFormat = 'DD-MM-YYYY HH:mm';
    return (
      <div className="zone-overview-div-container">
        <h3>
          {data.zone.name}&nbsp;
          <small>({moment(dateRange.min).format(longDateFormat)} - {moment(dateRange.max).format(longDateFormat)})</small>
        </h3>
        <ReactApexChart options={{
          chart: {
            type: 'area',
            stacked: false,
            height: 350,
            zoom: {
              type: 'x',
              enabled: true,
              autoScaleYaxis: true,
            },
            toolbar: {
              autoSelected: 'zoom',
              tools: {
                download: false,
                pan: false,
              },
            },
            events: {
              zoomed(chartContext: any, { xaxis }: any) {

                setDateRange({
                  min: xaxis.min || minRange,
                  max: xaxis.max || maxRange,
                });

              },
            },
          },
          dataLabels: {
            enabled: false,
          },
          markers: {
            size: 0.3,
          },
          fill: {
            type: 'gradient',
            gradient: {
              shadeIntensity: 1,
              inverseColors: false,
              opacityFrom: 0.5,
              opacityTo: 0,
              stops: [0, 90, 100],
            },
          },
          yaxis: {
            stepSize: 1,
            max: Math.max(Math.ceil(maxY * 1.2), Math.ceil(settings.minOccupancy * 1.2)),
          },
          xaxis: {
            type: 'datetime',
            labels: {
              datetimeUTC: false,
              datetimeFormatter: {
                year: 'yyyy',
                month: "MMM 'yy",
                day: 'dddd dd MMM',
                hour: 'HH:mm',
                minute: 'HH:mm:ss',
                second: 'HH:mm:ss',
              },
            },
          },
          tooltip: {
            shared: false,
            enabled: true,

            x: {
              show: true,
              format: 'dddd dd MMM HH:mm',
              formatter: undefined,
            },
            custom: (opts) => {

              const {
                series, seriesIndex, dataPointIndex, w,
              } = opts;

              const currentValue = series[seriesIndex][dataPointIndex];
              const currentTimestamp = w.config.series[seriesIndex].data[dataPointIndex].x;
              const assetsIndexes = w.config.series[seriesIndex].data[dataPointIndex].assetIndexes;

              return `<div>
                <div class="apexcharts-tooltip-title">
                  ${moment(currentTimestamp).format(longDateFormat)}
                </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
                  <span class="apexcharts-tooltip-marker" style="background-color: ${theme.palette.primary.main}"></span>
                  <div class="apexcharts-tooltip-text">
                    <div class="apexcharts-tooltip-y-group">
                      <span class="apexcharts-tooltip-text-y-label">Bezetting: </span><span class="apexcharts-tooltip-text-y-value">${currentValue}</span>
                    </div>
                  </div>
                </div>
                ${assetsIndexes && assetsIndexes.length > 0 ? `
                <div style="padding: 5px">
                  <strong>Aanwezig:</strong><br />
                  ${assetsIndexes.map((index: number) => `- ${data.assets[index].name}`).join('<br />')}
                </div>` : ''}
              </div>`;

            },

          },
          colors: [
            theme.palette.primary.main,
            theme.palette.success.main,
          ],
        }}
        series={[
          {
            name: translate('resources.reports.fields.occupancy'),
            data: mappedData,
          },
          {
            name: translate('resources.reports.fields.minOccupancy'),
            data: minOccupancyLine,
          },
        ]}
        type="area"
        height={350} />
      </div>
    );

  };

  if (hasZones === false) {

    return (
      <>
        <div className="zone-overview-no-data">
          <span>
            {translate('resources.reports.text.zones_no_groups')}
          </span>
          <Button onClick={() => navigate('/zone-map')} label="resources.zones.text.title" variant={'contained'} size="medium" sx={{
            marginTop: '10px',
          }}/>
        </div>
      </>
    );

  }

  if (isLoading) {

    return (
      <>
        <ZoneSettings/>
        <Loading sx={{
          height: '90% !important',
        }}/>
      </>
    );

  }

  if (!reportResult) {

    return (
      <>
        <ZoneSettings/>
        <div className="zone-overview-no-data">
          <span>
            {translate('resources.reports.text.zones_no_data')}
          </span>
        </div>
      </>
    );

  }
  return (
    <div>
      <ZoneSettings/>
      {reportResult.value.zones.map((zoneValue: any) => (
        <ZoneGraph {...zoneValue} assets={reportResult.value.assets} />
      ))}
    </div>
  );

};
